import { createFileRoute, notFound } from "@tanstack/react-router"
import { allArticles } from "contentlayer/generated"
import dayjs from "dayjs"
import { Calendar, Clock } from "lucide-react"
import { MarkdownContent } from "@/components/MarkdownContent/MarkdownContent"
import { TableOfContents } from "@/components/TableOfContents/TableOfContents"
import { Tag } from "@/components/Tag/Tag"
import { TranslationNotice } from "@/components/TranslationNotice/TranslationNotice"
import { extractHeadings } from "@/utils/extractHeadings"

export const Route = createFileRoute("/articles/$slug")({
  component: ArticlePage,
  loader: ({ params }) => {
    const post = allArticles.find(
      (p) => p.id === params.slug && p.locale === "ja",
    )
    if (!post) throw notFound()
    const hasEnglishVersion = allArticles.some(
      (p) => p.id === params.slug && p.locale === "en",
    )
    return { post, hasEnglishVersion }
  },
  head: ({ loaderData }) => ({
    meta: loaderData
      ? [
          { title: loaderData.post.title },
          { name: "description", content: "ブログ記事" },
        ]
      : [{ title: "404" }],
  }),
})

function ArticlePage() {
  const { post, hasEnglishVersion } = Route.useLoaderData()
  const headings = extractHeadings(post.body.html)

  return (
    <article className="mx-auto w-full max-w-3xl px-4 py-8">
      <header className="mb-10 border-b border-gray-200 pb-6">
        {post.tags && post.tags.length > 0 && (
          <div className="mb-4 flex gap-2">
            {post.tags.map((tag) => (
              <Tag key={tag} label={tag} size="md" />
            ))}
          </div>
        )}

        <h1 className="mb-4 text-4xl font-extrabold leading-tight tracking-tight text-text-primary">
          {post.title}
        </h1>

        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm font-medium text-text-muted">
          <span className="flex items-center gap-1">
            <Calendar size={16} />
            {dayjs(post.publishedAt).format("YYYY-MM-DD")}
          </span>
          <span className="size-1 rounded-full bg-gray-300" />
          <span className="flex items-center gap-1">
            <Clock size={16} />
            {post.readingTime}分
          </span>
        </div>
      </header>

      {hasEnglishVersion && (
        <TranslationNotice targetLocale="en" href={`/en/articles/${post.id}`} />
      )}

      <TableOfContents headings={headings} initialOpen={false} />

      <main className="prose prose-lg max-w-none text-text-primary">
        <MarkdownContent post={post} />
      </main>
    </article>
  )
}
